import { DBSchema, deleteDB, IDBPDatabase, openDB } from "idb";
import type { PrototypeTools, WebinarRepository } from "./repository";
import {
  AdminError, EmailDelivery, EmailQuota, EmailType, Registration, RegistrationError, RegistrationFilters, REGISTRATION_STATUSES,
  reopensWebinar, SendProgress, Webinar,
} from "./types";
import { availability, isPublicWebinar } from "./availability";
import { demoWebinars, sampleAttendee, SIMULATED_FAILURE_DOMAIN } from "./seed";
import { PRIVACY_NOTICE_VERSION, PROTOTYPE_DAILY_EMAIL_LIMIT, PROTOTYPE_MONTHLY_EMAIL_LIMIT } from "./config";
import { validateRegistration } from "./shared/validation";
import { isSafeMeetingLink } from "./shared/emailTemplates";
import { validateWebinar } from "./webinarValidation";

// ══════════════════════════════════════════════════════════════════════════════
//   PROTOTYPE BACKEND
//   IndexedDB lives in this browser profile only: registrations made on a visitor's device
//   never reach the administrator's device, and no email is ever sent. Emails are simulated
//   so the admin tools can be tried. Production uses supabaseRepository.ts.
// ══════════════════════════════════════════════════════════════════════════════

const DB_NAME = "kole-medical-webinars";
/** Bump this and add a step in upgrade() whenever the schema changes */
const DB_VERSION = 4;
const CHANGE_CHANNEL = "kole-medical-webinars";

interface StoredMeetingLink {
  webinarId: string;
  link: string;
  updatedAt: string;
}

interface WebinarsDB extends DBSchema {
  webinars: {
    key: string;
    value: Webinar;
    indexes: { "by-slug": string; "by-status": string; "by-startAt": string };
  };
  registrations: {
    key: string;
    value: Registration;
    indexes: {
      "by-webinar": string;
      "by-email": string;
      "by-status": string;
      "by-registeredAt": string;
      "by-webinar-email": [string, string];
    };
  };
  /** Small key-value store: the reference counter and the demo-seed flag */
  meta: { key: string; value: number | boolean };
  /** Added in version 4: one row per email per registration, like Supabase's email_delivery_log */
  emailLog: {
    key: string;
    value: EmailDelivery;
    indexes: { "by-registration": string; "by-registration-type": [string, EmailType] };
  };
  /** Added in version 4: private joining links, one per webinar */
  meetingLinks: { key: string; value: StoredMeetingLink };
}

type DB = IDBPDatabase<WebinarsDB>;

/** Fields older versions stored on webinars, moved or renamed in version 4 */
type LegacyWebinar = Webinar & { presenterPhotoId?: string; meetingLink?: string; createdAt?: string; updatedAt?: string };

const LEGACY_PHOTO_KEYS: Record<string, string> = {
  "builtin-tebogo-kole": "tebogo-kole",
  "builtin-tebogo-kole-outdoor": "tebogo-kole-outdoor",
};

let dbPromise: Promise<DB> | null = null;

function database(): Promise<DB> {
  if (!dbPromise) {
    dbPromise = openDB<WebinarsDB>(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion, _newVersion, transaction) {
        // Each step only runs for databases older than it, so upgrading never wipes registrations.
        if (oldVersion < 1) {
          const webinars = db.createObjectStore("webinars", { keyPath: "id" });
          webinars.createIndex("by-slug", "slug", { unique: true });
          webinars.createIndex("by-status", "status");
          webinars.createIndex("by-startAt", "startAt");

          const registrations = db.createObjectStore("registrations", { keyPath: "id" });
          registrations.createIndex("by-webinar", "webinarId");
          registrations.createIndex("by-email", "email");
          registrations.createIndex("by-status", "status");
          registrations.createIndex("by-registeredAt", "registeredAt");
          registrations.createIndex("by-webinar-email", ["webinarId", "email"]);

          db.createObjectStore("meta");
        }
        // Versions 2–3 had an uploaded presenter photo library. Version 4 drops it (photos now ship
        // with the site), adds the email log and private meeting links, and moves old webinars over.
        if (oldVersion < 4) {
          const legacyDb = db as unknown as IDBPDatabase;
          if (legacyDb.objectStoreNames.contains("presenterPhotos")) legacyDb.deleteObjectStore("presenterPhotos");

          const emailLog = db.createObjectStore("emailLog", { keyPath: "id" });
          emailLog.createIndex("by-registration", "registrationId");
          emailLog.createIndex("by-registration-type", ["registrationId", "emailType"], { unique: true });
          db.createObjectStore("meetingLinks", { keyPath: "webinarId" });

          if (oldVersion >= 1) {
            transaction.objectStore("webinars").openCursor().then(async function migrate(cursor) {
              while (cursor) {
                const { presenterPhotoId, meetingLink, createdAt, updatedAt, ...webinar } = cursor.value as LegacyWebinar;
                if (meetingLink) {
                  await transaction.objectStore("meetingLinks").put({ webinarId: webinar.id, link: meetingLink, updatedAt: nowIso() });
                }
                await cursor.update({
                  ...webinar,
                  summary: webinar.summary ?? webinar.description,
                  presenterPhoto: webinar.presenterPhoto ?? (presenterPhotoId ? LEGACY_PHOTO_KEYS[presenterPhotoId] : undefined),
                });
                cursor = await cursor.continue();
              }
            });
          }
        }
        // Future schema changes go here, e.g. `if (oldVersion < 5) { ... }`
      },
      // Another tab opened a newer schema: let it upgrade, then reconnect on the next call
      blocking() {
        dbPromise?.then(db => db.close());
        dbPromise = null;
      },
    }).then(async db => {
      await seedDemoWebinars(db, false);
      return db;
    });
    // Let a failed open be retried later instead of caching the failure
    dbPromise.catch(() => { dbPromise = null; });
  }
  return dbPromise;
}

// ══════════════════════════════════════════════════════════════════════════════
//   HELPERS
// ══════════════════════════════════════════════════════════════════════════════

const newId = () => {
  const c = typeof crypto !== "undefined" ? (crypto as Crypto & { randomUUID?: () => string }) : undefined;
  return c?.randomUUID ? c.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

const nowIso = () => new Date().toISOString();
const reference = (seq: number) => `KMW-${String(seq).padStart(5, "0")}`;
const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/** Takes a seat and can be emailed: not cancelled, and not archived from an earlier round */
const isCurrent = (r: Registration) => r.status !== "cancelled" && !r.archivedAt;

// ── Email limits, simulated like Resend's: per UTC day (reset at 00:00 UTC) and per calendar month ──
const quotaKey = () => `emailsSent:${new Date().toISOString().slice(0, 10)}`;
const monthQuotaKey = () => `emailsSentMonth:${new Date().toISOString().slice(0, 7)}`;
const nextUtcMidnight = () => {
  const d = new Date();
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + 1)).toISOString();
};
const nextUtcMonth = () => {
  const d = new Date();
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1)).toISOString();
};
/** How many more emails both limits allow, from today's and this month's counts */
const allowance = (day: number, month: number) =>
  Math.max(0, Math.min(PROTOTYPE_DAILY_EMAIL_LIMIT - day, PROTOTYPE_MONTHLY_EMAIL_LIMIT - month));
/** Counted like Resend: only emails it accepted (simulated ones), not refusals */
const countsTowardQuota = (email: EmailDelivery) => email.status === "simulated";

// Change notifications for this tab and, through BroadcastChannel, every other open tab
const listeners = new Set<() => void>();
const channel = typeof BroadcastChannel !== "undefined" ? new BroadcastChannel(CHANGE_CHANNEL) : null;
channel?.addEventListener("message", () => listeners.forEach(listener => listener()));

function notifyChange() {
  listeners.forEach(listener => listener());
  channel?.postMessage("changed");
}

async function seedDemoWebinars(db: DB, force: boolean) {
  const tx = db.transaction(["webinars", "meta"], "readwrite");
  // Checked inside the same transaction, so two tabs opening at once can't both seed
  if (!force && (await tx.objectStore("meta").get("seeded"))) {
    await tx.done;
    return;
  }
  for (const input of demoWebinars()) {
    tx.objectStore("webinars").put({ ...input, id: newId() });
  }
  tx.objectStore("meta").put(true, "seeded");
  await tx.done;
}

/**
 * Pretends to send an email. At example.invalid addresses, the first attempt of each email fails
 * and the retry succeeds, so the failure and retry tools can be demonstrated. Deliberate resends
 * (send round 2 and later) go straight through.
 */
function simulateSend(email: EmailDelivery, address: string): EmailDelivery {
  const attemptCount = email.attemptCount + 1;
  const updatedAt = nowIso();
  if (address.endsWith(SIMULATED_FAILURE_DOMAIN) && email.sendRound === 1 && attemptCount === 1) {
    return { ...email, attemptCount, updatedAt, status: "failed", lastError: "Simulated temporary failure: the recipient's mail server didn't respond." };
  }
  return { ...email, attemptCount, updatedAt, status: "simulated", sentAt: updatedAt, lastError: undefined };
}

const newEmail = (registrationId: string, emailType: EmailType): EmailDelivery =>
  ({ id: newId(), registrationId, emailType, status: "queued", attemptCount: 0, sendRound: 1, updatedAt: nowIso() });

// ══════════════════════════════════════════════════════════════════════════════
//   REPOSITORY
// ══════════════════════════════════════════════════════════════════════════════

export function createIndexedDbRepository(): WebinarRepository & PrototypeTools {
  /** Sends every queued email in scope, one at a time, reporting progress as it goes */
  async function processQueue(
    scope: { webinarId: string | null; emailType: EmailType | null },
    queued: number,
    onProgress?: (progress: SendProgress) => void,
  ): Promise<SendProgress> {
    const db = await database();
    const registrations = new Map((await db.getAll("registrations")).map(r => [r.id, r]));
    const cancelledWebinars = await cancelledWebinarIds();
    const due = (await db.getAll("emailLog")).filter(email => {
      const registration = registrations.get(email.registrationId);
      return email.status === "queued" && registration && isCurrent(registration) &&
        !(email.emailType === "joining_instructions" && cancelledWebinars.has(registration.webinarId)) &&
        (!scope.webinarId || registration.webinarId === scope.webinarId) &&
        (!scope.emailType || email.emailType === scope.emailType);
    });

    const progress: SendProgress = { queued, sent: 0, failed: 0, remaining: due.length, emailEnabled: true };
    onProgress?.({ ...progress });
    for (const email of due) {
      // At a limit the rest stay queued until it resets, as in production
      const used = ((await db.get("meta", quotaKey())) as number | undefined) ?? 0;
      const monthUsed = ((await db.get("meta", monthQuotaKey())) as number | undefined) ?? 0;
      if (allowance(used, monthUsed) === 0) break;
      await sleep(40); // a little visible progress, as with real batches
      const result = simulateSend(email, registrations.get(email.registrationId)!.email);
      await db.put("emailLog", result);
      if (countsTowardQuota(result)) {
        await db.put("meta", used + 1, quotaKey());
        await db.put("meta", monthUsed + 1, monthQuotaKey());
      }
      progress[result.status === "failed" ? "failed" : "sent"]++;
      progress.remaining--;
      onProgress?.({ ...progress });
    }
    progress.quota = await quotaStatus();
    notifyChange();
    return progress;
  }

  /** Joining instructions are never sent for these, as in the database */
  async function cancelledWebinarIds() {
    const webinars = await (await database()).getAll("webinars");
    return new Set(webinars.filter(w => w.status === "cancelled").map(w => w.id));
  }

  async function quotaStatus(): Promise<EmailQuota> {
    const db = await database();
    const used = ((await db.get("meta", quotaKey())) as number | undefined) ?? 0;
    const monthUsed = ((await db.get("meta", monthQuotaKey())) as number | undefined) ?? 0;
    const registrations = new Map((await db.getAll("registrations")).map(r => [r.id, r]));
    const cancelledWebinars = await cancelledWebinarIds();
    const waiting = (await db.getAll("emailLog")).filter(email => {
      const registration = registrations.get(email.registrationId);
      return email.status === "queued" && registration && isCurrent(registration) &&
        !(email.emailType === "joining_instructions" && cancelledWebinars.has(registration.webinarId));
    }).length;
    return {
      dailyLimit: PROTOTYPE_DAILY_EMAIL_LIMIT,
      used,
      dayRemaining: Math.max(0, PROTOTYPE_DAILY_EMAIL_LIMIT - used),
      resetsAt: nextUtcMidnight(),
      exhausted: false,
      monthlyLimit: PROTOTYPE_MONTHLY_EMAIL_LIMIT,
      monthUsed,
      monthRemaining: Math.max(0, PROTOTYPE_MONTHLY_EMAIL_LIMIT - monthUsed),
      monthResetsAt: nextUtcMonth(),
      monthExhausted: false,
      inFlight: 0,
      remaining: allowance(used, monthUsed),
      waiting,
    };
  }

  const repo: WebinarRepository & PrototypeTools = {
    backend: "prototype",

    // ── Public ──

    async listPublicWebinars() {
      const db = await database();
      return (await db.getAllFromIndex("webinars", "by-startAt")).filter(isPublicWebinar);
    },

    async getPublicWebinar(slug) {
      const webinar = await (await database()).getFromIndex("webinars", "by-slug", slug);
      return webinar && isPublicWebinar(webinar) ? webinar : undefined;
    },

    async countActiveRegistrations() {
      const db = await database();
      const counts: Record<string, number> = {};
      for (const r of await db.getAll("registrations")) {
        if (isCurrent(r)) counts[r.webinarId] = (counts[r.webinarId] ?? 0) + 1;
      }
      return counts;
    },

    async register(data, meta) {
      // The same checks the production server makes, in the same order
      if (meta.website.trim() || meta.elapsedMs < 1500) {
        throw new RegistrationError("invalid", "We couldn't accept this registration. Please try again.");
      }
      const result = validateRegistration(data);
      if (!result.ok) throw new RegistrationError("invalid", "Please check the highlighted fields.", result.errors);
      const input = result.value;

      const db = await database();
      // One transaction, so the duplicate check, capacity check and reference number can't race
      const tx = db.transaction(["webinars", "registrations", "meta", "emailLog"], "readwrite");
      const webinar = await tx.objectStore("webinars").get(input.webinarId);
      if (!webinar || webinar.status === "draft") {
        throw new RegistrationError("not_found", "This webinar could not be found.");
      }

      const registrations = tx.objectStore("registrations");
      const sameEmail = await registrations.index("by-webinar-email").getAll([input.webinarId, input.email]);
      if (sameEmail.some(isCurrent)) {
        throw new RegistrationError("duplicate", "This email address is already registered for this webinar.");
      }

      const active = (await registrations.index("by-webinar").getAll(input.webinarId)).filter(isCurrent);
      const state = availability(webinar, active.length);
      if (!state.canRegister) {
        throw new RegistrationError(state.isFull ? "full" : "closed", state.closedReason ?? "Registration is closed.");
      }

      const seq = (((await tx.objectStore("meta").get("registrationSeq")) as number | undefined) ?? 0) + 1;
      const now = nowIso();
      const record: Registration = {
        id: newId(),
        reference: reference(seq),
        webinarId: webinar.id,
        firstName: input.firstName,
        lastName: input.lastName,
        email: input.email,
        phone: input.phone,
        organisation: input.organisation,
        professionalRole: input.professionalRole,
        status: "registered",
        privacyNoticeVersion: PRIVACY_NOTICE_VERSION,
        marketingConsent: input.marketingConsent,
        registeredAt: now,
        updatedAt: now,
      };
      const sentToday = ((await tx.objectStore("meta").get(quotaKey())) as number | undefined) ?? 0;
      const sentThisMonth = ((await tx.objectStore("meta").get(monthQuotaKey())) as number | undefined) ?? 0;
      const confirmation = allowance(sentToday, sentThisMonth) > 0
        ? simulateSend(newEmail(record.id, "confirmation"), record.email)
        : newEmail(record.id, "confirmation"); // over a limit: queued until it resets
      if (countsTowardQuota(confirmation)) {
        tx.objectStore("meta").put(sentToday + 1, quotaKey());
        tx.objectStore("meta").put(sentThisMonth + 1, monthQuotaKey());
      }
      tx.objectStore("meta").put(seq, "registrationSeq");
      await registrations.add(record);
      await tx.objectStore("emailLog").add(confirmation);
      await tx.done;
      notifyChange();
      return {
        reference: record.reference,
        registeredAt: record.registeredAt,
        confirmationEmail: confirmation.status === "failed" ? "failed" : confirmation.status === "queued" ? "queued" : "simulated",
      };
    },

    // ── Admin (no sign-in in the prototype) ──

    async getAdminSession() {
      return { email: "Prototype (no sign-in)", isAdmin: true };
    },

    async signIn() {},
    async signOut() {},

    async listAllWebinars() {
      return (await database()).getAllFromIndex("webinars", "by-startAt");
    },

    async saveWebinar(input, id) {
      if (Object.keys(validateWebinar(input)).length > 0) throw new AdminError("invalid", "Check the highlighted fields.");
      const db = await database();
      const tx = db.transaction(["webinars", "registrations"], "readwrite");
      const webinars = tx.objectStore("webinars");
      const existing = id ? await webinars.get(id) : undefined;
      if (id && !existing) throw new AdminError("invalid", "This webinar no longer exists.");
      const clash = await webinars.index("by-slug").get(input.slug);
      if (clash && clash.id !== id) throw new AdminError("duplicate", "Another webinar already uses this web address.");

      // Edited demo webinars stay labelled as demos; webinars added here are real
      const webinar: Webinar = { ...input, id: existing?.id ?? newId(), isDemo: existing?.isDemo ?? false };
      await webinars.put(webinar);

      // Reopening an archived webinar starts a new round (mirrors the database trigger)
      if (existing && reopensWebinar(existing.status, webinar.status)) {
        const now = nowIso();
        const registrations = tx.objectStore("registrations");
        for (const r of await registrations.index("by-webinar").getAll(webinar.id)) {
          if (!r.archivedAt) await registrations.put({ ...r, archivedAt: now, roundStartAt: existing.startAt, updatedAt: now });
        }
      }
      await tx.done;
      notifyChange();
      return webinar;
    },

    async listRegistrations(filters: RegistrationFilters = {}) {
      const db = await database();
      // The index returns oldest first
      const all = await db.getAllFromIndex("registrations", "by-registeredAt");
      const query = filters.search?.trim().toLowerCase();
      const round = filters.round ?? "current";
      const rows = all.filter(r =>
        (round === "all" || (round === "archived") === Boolean(r.archivedAt)) &&
        (!filters.webinarId || r.webinarId === filters.webinarId) &&
        (!filters.status || r.status === filters.status) &&
        (!query ||
          `${r.firstName} ${r.lastName}`.toLowerCase().includes(query) ||
          r.email.includes(query) ||
          r.reference.toLowerCase().includes(query)));
      return filters.sort === "oldest" ? rows : rows.reverse();
    },

    async updateRegistration(id, data) {
      const db = await database();
      const tx = db.transaction("registrations", "readwrite");
      const existing = await tx.store.get(id);
      if (!existing) throw new AdminError("invalid", "This registration no longer exists.");

      const next: Registration = { ...existing, updatedAt: nowIso() };
      if (data.status) {
        if (!REGISTRATION_STATUSES.includes(data.status)) throw new AdminError("invalid", "Choose a valid status.");
        // Restoring a cancelled registration mustn't create a second active one for the same person
        if (existing.status === "cancelled" && data.status !== "cancelled" && !existing.archivedAt) {
          const sameEmail = await tx.store.index("by-webinar-email").getAll([existing.webinarId, existing.email]);
          if (sameEmail.some(r => r.id !== id && isCurrent(r))) {
            throw new AdminError("duplicate", "This person already has another active registration for this webinar.");
          }
        }
        next.status = data.status;
      }
      if (data.instructionsSentAt === null) delete next.instructionsSentAt;
      else if (data.instructionsSentAt) next.instructionsSentAt = data.instructionsSentAt;

      await tx.store.put(next);
      await tx.done;
      notifyChange();
    },

    async listEmailDeliveries() {
      return (await database()).getAll("emailLog");
    },

    async getMeetingLinks() {
      const links = await (await database()).getAll("meetingLinks");
      return Object.fromEntries(links.map(l => [l.webinarId, l.link]));
    },

    async saveMeetingLink(webinarId, link) {
      const db = await database();
      const trimmed = link.trim();
      if (!trimmed) {
        await db.delete("meetingLinks", webinarId);
      } else {
        if (!isSafeMeetingLink(trimmed)) throw new AdminError("invalid", "Paste the full joining link, starting with https://");
        await db.put("meetingLinks", { webinarId, link: trimmed, updatedAt: nowIso() });
      }
      notifyChange();
    },

    async sendJoiningInstructions(request, onProgress) {
      const db = await database();
      if ((await db.get("webinars", request.webinarId))?.status === "cancelled") {
        throw new AdminError("webinar_cancelled", "This webinar is cancelled, so joining instructions can't be sent.");
      }
      if (!(await db.get("meetingLinks", request.webinarId))) {
        throw new AdminError("no_meeting_link", "Save the joining link for this webinar first.");
      }

      // Queue: everyone registered (or the selected people), skipping anyone already sent
      // instructions unless this is a deliberate resend. Mirrors queue_joining_instructions().
      const tx = db.transaction(["registrations", "emailLog"], "readwrite");
      const eligible = (await tx.objectStore("registrations").index("by-webinar").getAll(request.webinarId))
        .filter(r => r.status === "registered" && !r.archivedAt && (!request.registrationIds || request.registrationIds.includes(r.id)));
      let queued = 0;
      for (const registration of eligible) {
        const existing = await tx.objectStore("emailLog").index("by-registration-type").get([registration.id, "joining_instructions"]);
        if (!existing) {
          await tx.objectStore("emailLog").add(newEmail(registration.id, "joining_instructions"));
          queued++;
        } else if (request.resend && existing.status !== "queued" && existing.status !== "sending") {
          await tx.objectStore("emailLog").put({
            ...existing, status: "queued", sendRound: existing.sendRound + 1, attemptCount: 0,
            lastError: undefined, sentAt: undefined, updatedAt: nowIso(),
          });
          queued++;
        }
      }
      await tx.done;

      return processQueue({ webinarId: request.webinarId, emailType: "joining_instructions" }, queued, onProgress);
    },

    async retryFailedEmails(webinarId, onProgress) {
      const db = await database();
      const cancelledWebinars = await cancelledWebinarIds();
      const registrations = new Map((await db.getAll("registrations")).map(r => [r.id, r]));
      for (const email of await db.getAll("emailLog")) {
        const registration = registrations.get(email.registrationId);
        const joiningForCancelled = email.emailType === "joining_instructions" && cancelledWebinars.has(registration?.webinarId ?? "");
        if (email.status === "failed" && registration && isCurrent(registration) && !joiningForCancelled && (!webinarId || registration.webinarId === webinarId)) {
          await db.put("emailLog", { ...email, status: "queued", updatedAt: nowIso() });
        }
      }
      return processQueue({ webinarId, emailType: null }, 0, onProgress);
    },

    async sendWaitingEmails(webinarId, onProgress) {
      return processQueue({ webinarId, emailType: null }, 0, onProgress);
    },

    getEmailQuota: quotaStatus,

    // The prototype has no Supabase project to measure
    async getProjectUsage() {
      return null;
    },

    async recordProjectUsage() {
      throw new AdminError("unavailable", "Usage tracking needs the live Supabase project.");
    },

    async listAdminActivity() {
      return null;
    },

    subscribe(listener) {
      listeners.add(listener);
      return () => { listeners.delete(listener); };
    },

    // ── Prototype tools ──

    async deleteLocalDatabase() {
      // Close this tab's connection first; other tabs close theirs through blocking()
      const open = dbPromise;
      dbPromise = null;
      try {
        (await open)?.close();
      } catch {
        // The database never opened, so there's nothing to close
      }
      await deleteDB(DB_NAME);
    },

    async resetToDemoData() {
      const db = await database();
      const tx = db.transaction(["webinars", "registrations", "meta", "emailLog", "meetingLinks"], "readwrite");
      await Promise.all([
        tx.objectStore("webinars").clear(),
        tx.objectStore("registrations").clear(),
        tx.objectStore("meta").clear(),
        tx.objectStore("emailLog").clear(),
        tx.objectStore("meetingLinks").clear(),
        tx.done,
      ]);
      await seedDemoWebinars(db, true);
      await syncContentWebinars(db);
      notifyChange();
    },

    async addSampleRegistrations() {
      const db = await database();
      const tx = db.transaction(["webinars", "registrations", "meta", "emailLog"], "readwrite");
      const webinars = (await tx.objectStore("webinars").getAll()).filter(w => w.isDemo && w.status !== "draft");
      const registrations = tx.objectStore("registrations");
      let seq = ((await tx.objectStore("meta").get("registrationSeq")) as number | undefined) ?? 0;
      let added = 0;

      for (const [i, webinar] of webinars.entries()) {
        const past = webinar.status === "completed";
        for (let k = 1; k <= 4; k++) {
          const n = i * 4 + k;
          const sample = sampleAttendee(n);
          const existing = await registrations.index("by-webinar-email").getAll([webinar.id, sample.email]);
          if (existing.length > 0) continue;
          seq += 1;
          const registeredAt = new Date(Math.min(Date.now(), Date.parse(webinar.startAt) - (2 + n) * 864e5)).toISOString();
          const record: Registration = {
            id: newId(),
            reference: reference(seq),
            webinarId: webinar.id,
            firstName: sample.firstName,
            lastName: sample.lastName,
            email: sample.email,
            phone: sample.phone,
            organisation: sample.organisation,
            professionalRole: sample.professionalRole,
            status: past && sample.status !== "cancelled" ? "attended" : sample.status,
            privacyNoticeVersion: PRIVACY_NOTICE_VERSION,
            marketingConsent: sample.marketingConsent,
            registeredAt,
            updatedAt: registeredAt,
          };
          await registrations.add(record);
          await tx.objectStore("emailLog").add({ ...simulateSend(newEmail(record.id, "confirmation"), record.email), updatedAt: registeredAt });
          added++;
        }
      }
      tx.objectStore("meta").put(seq, "registrationSeq");
      await tx.done;
      notifyChange();
      return added;
    },
  };
  return repo;
}
